import { Link2 } from "lucide-react";

import { cn } from "@/shared/lib/utils";

interface SectionHeadingProps {
	/** Stable id used as the scroll anchor + the TOC scroll-spy target. */
	id: string;
	/** Optional purple eyebrow (dot + uppercase tracked label) above the title. */
	eyebrow?: string;
	/** Section heading text (rendered in the Geist display face). */
	children: string;
	className?: string;
}

/**
 * Anchored section heading for a bespoke doc page. Renders a heading block
 * (optional eyebrow + `<h2>`) carrying a stable `id` — the scroll-spy / deep-link
 * target — plus a hover-revealed anchor affordance. Headings use the brand Geist
 * display face (semibold, tight tracking) via the default sans token, matching
 * above.security/resources, which has no serif.
 *
 * The `id` and `scroll-mt-6` (sticky-header offset) sit on the SAME element so a
 * deep link / `scrollIntoView` lands the whole block below the header rather than
 * under it — mirroring TrustSection/GuidesIndexPage.
 *
 * The purple eyebrow dot is the only accent here — signal over decoration.
 */
export function SectionHeading({ id, eyebrow, children, className }: SectionHeadingProps) {
	return (
		<div id={id} className={cn("group/heading scroll-mt-6", className)}>
			{eyebrow && (
				<div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
					<span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
					{eyebrow}
				</div>
			)}
			<h2
				className={cn(
					"text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl",
					eyebrow && "mt-3",
				)}
			>
				<a
					href={`#${id}`}
					data-mp-id={`doc-anchor-${id}`}
					className="group/anchor inline-flex items-center gap-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
				>
					{children}
					<Link2
						className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover/heading:opacity-60 group-focus-within/heading:opacity-60"
						aria-hidden="true"
					/>
				</a>
			</h2>
		</div>
	);
}
